import React, { useState, useRef, useEffect } from 'react';
import { Treemap, ResponsiveContainer, Tooltip } from 'recharts';
import { useTheme } from '../../../context/ThemeContext';
import CustomCard from '../../Scanners/components/CustomCard';

/**
 * HeatMap Section Component - Shows treemap visualization grouped by parent
 * Ported from Flutter stock_page.dart heatmap implementation
 */
const HeatMapSection = ({ heatMapData, updatedOn }) => {
  const { theme } = useTheme();
  const [isScrolling, setIsScrolling] = useState(false);
  const [activeSection, setActiveSection] = useState(null);
  const scrollTimeoutRef = useRef(null);
  const containerRef = useRef(null);

  // Handle scroll events to hide tooltips while scrolling
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolling(true);

      // Clear existing timeout
      if (scrollTimeoutRef.current) {
        clearTimeout(scrollTimeoutRef.current);
      }

      // Set timeout to hide scrolling state after user stops scrolling
      scrollTimeoutRef.current = setTimeout(() => {
        setIsScrolling(false);
      }, 150);
    };

    const container = containerRef.current;
    if (container) {
      container.addEventListener('scroll', handleScroll, { passive: true });
    }

    return () => {
      if (container) {
        container.removeEventListener('scroll', handleScroll);
      }
      if (scrollTimeoutRef.current) {
        clearTimeout(scrollTimeoutRef.current);
      }
    };
  }, []);

  if (!heatMapData || heatMapData.length === 0) {
    return null;
  }

  // Group heatmap data by parent (same as Flutter implementation)
  const groupByParent = () => {
    const treeMaps = {};

    heatMapData.forEach(item => {
      if (item.parent) {
        if (treeMaps[item.parent]) {
          treeMaps[item.parent].push({
            name: item.name,
            size: parseFloat(item.size || '0'),
            change: item.change,
            color: item.color || '#CCCCCC',
          });
        } else {
          treeMaps[item.parent] = [{
            name: item.name,
            size: parseFloat(item.size || '0'),
            change: item.change,
            color: item.color || '#CCCCCC',
          }];
        }
      }
    });

    return treeMaps;
  };

  const groupedData = groupByParent();
  const parentKeys = Object.keys(groupedData);

  // Custom content for treemap cells
  const CustomTreemapContent = (props) => {
    const { x, y, width, height, name, change, color } = props;

    // Don't render if too small
    if (width < 40 || height < 30) {
      return null;
    }

    return (
      <g>
        <rect
          x={x}
          y={y}
          width={width}
          height={height}
          style={{
            fill: color,
            stroke: theme === 'dark' ? '#0F0F0F' : '#FFFFFF',
            strokeWidth: 2,
          }}
        />
        <text
          x={x + width / 2}
          y={y + height / 2 - 8}
          textAnchor="middle"
          fill="#FFFFFF"
          fontSize={12}
          fontWeight="500"
        >
          {name}
        </text>
        <text
          x={x + width / 2}
          y={y + height / 2 + 8}
          textAnchor="middle"
          fill="#FFFFFF"
          fontSize={11}
          fontWeight="400"
        >
          {change}%
        </text>
      </g>
    );
  };

  // Custom tooltip - accepts section parameter to ensure only one tooltip is visible
  const CustomTooltip = (section) => ({ active, payload }) => {
    // Hide tooltip while scrolling
    if (isScrolling) {
      return null;
    }

    // Only show tooltip if this section is the active one
    if (activeSection !== section) {
      return null;
    }

    if (active && payload && payload.length) {
      const data = payload[0].payload;
      return (
        <div
          className={`px-3 py-2 rounded shadow-lg ${
            theme === 'dark' ? 'bg-[#EDEDED] text-[#0F0F0F]' : 'bg-[#272727] text-white'
          }`}
        >
          <p className="font-semibold text-sm">{data.name}</p>
          <p className="text-xs">{data.change}%</p>
        </div>
      );
    }
    return null;
  };

  return (
    <div className="mt-6">
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-sm font-semibold text-p300">Heat Map</h3>
        {updatedOn && (
          <p className={`text-xs font-medium ${theme === 'dark' ? 'text-[#7F7F7F]' : 'text-[#878787]'}`}>
            Last updated {updatedOn}
          </p>
        )}
      </div>

      <CustomCard>
        <div ref={containerRef} className="w-full overflow-auto">
          {parentKeys.map((parent, index) => (
            <div
              key={parent}
              className={index > 0 ? 'mt-6' : ''}
              onMouseEnter={() => setActiveSection(parent)}
              onMouseLeave={() => setActiveSection(null)}
            >
              <p className={`text-xs font-medium mb-2 ${theme === 'dark' ? 'text-[#EDEDED]' : 'text-[#272727]'}`}>
                {parent}
              </p>
              <ResponsiveContainer width="100%" height={200}>
                <Treemap
                  data={groupedData[parent]}
                  dataKey="size"
                  stroke={theme === 'dark' ? '#0F0F0F' : '#FFFFFF'}
                  fill="#8884d8"
                  content={<CustomTreemapContent />}
                >
                  <Tooltip content={CustomTooltip(parent)} />
                </Treemap>
              </ResponsiveContainer>
            </div>
          ))}
        </div>
      </CustomCard>
    </div>
  );
};

export default HeatMapSection;
